/**
 * reusable login flow component
 *
 * On successful login this component forwards the the redirect route or to the root if there is no redirect.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */
// import primary libraries
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// actions
import { sendLogin, sendGetLoginOptions } from '../authStore';
import { useLoggedInUser } from '../authService';

// utils
import apiUtils from '../../../global/utils/api';
import { isSafeUrl, safeRedirect } from '../../../global/utils/linkUtils.js';


// import user components
import { EmailInput, PasswordInput } from '../../../global/components/forms';
import Spinner from '../../../global/components/helpers/Spinner.jsx';
import StyledButton from 'shared-resources/components/src/StyledButton';

// the SAML provider key for Simulations Plus' own IdP - registered under this
// exact string in server/config/*.js and special-cased in sessionController.
// Only this provider gets the S+ mark; other orgs' SSO buttons stay unbranded.
const SLP_SSO_PROVIDER = 'simulations-plus';

const UserLoginFlow = ({
  resetLink
  , loginRedirect
  , originRedirect
  , wrapperClass
  , logoUrl = 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png' // fall back to the pro-ficiency logo - does not work if null,
  , welcomeMessage = ''
  // the S+ Cloud redesign renders the wordmark above the card, so the view that
  // owns the wordmark opts out of the in-card logo. Callers that still brand the
  // card itself (group landing pages) leave this true and are unaffected.
  , showLogo = true
  // optional node shown at the end of the email step only (the sign-up prompt on
  // /user/login). Kept as a slot rather than hardcoded because group landing pages
  // reach sign-up through their own modal and must not get a second link here.
  , emailStepFooter = null
}) => {

  const dispatch = useDispatch();
  const location = useLocation();

  const finalLogoUrl = logoUrl || 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png';

  // check for email in params to pre-fill the username field on the login form
  const { email, error: rawSSOError } = apiUtils.objectFromQueryString(location.search);

  const [userEmail, setUserEmail] = useState(email || '');
  const [inputEmail, setInputEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [loginOptions, setLoginOptions] = useState(null);


  const submitUserEmail = (e) => {
    e?.preventDefault && e.preventDefault();
    setUserEmail(inputEmail);
  }

  const loggedInUser = useLoggedInUser();
  const [user, setUser] = useState({ username: email || '', password: '' });
  const [error, setError] = useState('');
  const [SSOError, setSSOError] = useState(null);

  const getLoginOptions = async () => {
    if (userEmail) {
      const { payload: loginOptions, error } = await dispatch(sendGetLoginOptions(userEmail));
      if (error) setError(error?.message || "There was a problem logging in. Please try again");
      setLoginOptions(loginOptions || {});
    }
  }

  useEffect(() => {
    if (userEmail) {
      setUser({ ...user, username: userEmail });
      getLoginOptions();
    }
  }, [userEmail]);

  // keep the SSO error in sync with the raw error from the query string but this way we can allow user to clear it as well
  useEffect(() => {
    if (rawSSOError !== SSOError) {
      setSSOError(rawSSOError);
    }
  }, [rawSSOError]);


  useEffect(() => {
    // this will run on mount and redirect if user is already logged in
    // it will also run on every update to loggedInUser (when the user logs in)
    if (loggedInUser) {
      // can't use react-router's history object here because we may be directing to an external site.
      // history.push(searchObject.redirect || "/");
      // console.debug('User logged in, redirecting to', { loginRedirect });
      safeRedirect(loginRedirect || "/");
    }
  }, [loggedInUser, loginRedirect]);

  const handleSubmit = async (e) => {
    setError('');
    e.preventDefault();
    const { error } = await dispatch(sendLogin(user));
    // generic error message for security reasons
    if (error) {
      setError(error?.message || "There was a problem logging in. Please try again");
      // reset password field on error
      setUser({ ...user, password: '' });
    }
  }

  const resetForm = () => {
    setUser({ username: '', password: '' });
    setError('');
    setUserEmail('');
    setInputEmail('');
    setLoginOptions(null);
  }

  const forgotPasswordLink = (useCallback(() => {
    if (originRedirect) {
      // we have to encode the url to preserve its query string
      const loginLink = encodeURIComponent(originRedirect);
      return `/user/forgot-password${location.search ? location.search + `&email=${userEmail}&loginLink=${loginLink}` : `?email=${userEmail}&loginLink=${loginLink}`}`;
    } else {
      return `/user/forgot-password${location.search ? location.search + `&email=${userEmail}` : `?email=${userEmail}`}`;
    }
  }, [userEmail, originRedirect]))();

  // the prototype shows SSO as the primary route and demotes password sign-in to
  // secondary beneath an "or" divider, but only when both are actually offered
  const hasSSO = loginOptions?.SSOLinks?.length > 0;
  const hasOtherOptions = !!(
    loginOptions?.hasPassword
    || loginOptions?.hasPassword === false
    || loginOptions?.allowMagicLink
  );

  if (loggedInUser) {
    // displayed while redirecting
    return <Spinner />;
  }

  return (
    <div className={wrapperClass}>
      {!userEmail ? (
        <>
          <div className='flex flex-col gap-6 w-full'>
            {showLogo && (
              <div className='flex justify-center md:p-4'>
                <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
              </div>
            )}
            <div className='flex flex-col gap-3'>
              <h1 className='text-2xl font-semibold text-neutral-pri m-0'>Welcome to S+ Cloud</h1>
              <p className='text-sm text-neutral-sec m-0'>
                {welcomeMessage || 'Access and manage supported Simulations Plus cloud products, AI-assisted tools, and services.'}
              </p>
            </div>
            <form name='userLoginForm' onSubmit={submitUserEmail} className='w-full flex flex-col gap-4'>
              <div className='flex flex-col gap-1.5 [&>div]:mb-0'>
                <label htmlFor='username' className='text-xs font-medium uppercase tracking-wider text-neutral-ter'>Email</label>
                <EmailInput
                  name='username'
                  value={inputEmail}
                  change={e => setInputEmail(e.target.value)}
                  onValidationChange={setIsEmailValid}
                  autoFocus={true}
                  placeholder="name@company.com"
                />
              </div>
              <StyledButton
                variant='primary'
                className='w-full'
                disabled={!inputEmail || !isEmailValid}
              >
                Continue &rarr;
              </StyledButton>
            </form>
            {emailStepFooter}
          </div>
          {error && (
            <p className='flex items-start gap-1.5 m-0 mt-4 text-xs text-error' role='alert'>
              <i className="fas fa-exclamation-circle mt-0.5" aria-hidden="true"></i>
              <span>{error}</span>
            </p>
          )}
          {SSOError && (
            <div className='flex items-start justify-between gap-1.5 mt-4 text-xs text-error' role='alert'>
              <span className='flex items-start gap-1.5'>
                <i className="fas fa-exclamation-circle mt-0.5" aria-hidden="true"></i>
                <span>{getFriendlyError(SSOError)}</span>
              </span>
              <button
                className='flex-none text-neutral-ter hover:text-neutral-pri'
                type='button'
                aria-label='Dismiss'
                onClick={(e) => {
                  e.preventDefault();
                  setSSOError(null);
                }}>
                <i className='fas fa-times' aria-hidden="true"></i>
              </button>
            </div>
          )}
        </>
      ) : !loginOptions ? (
        <>
          <LoginOptionsHeader userEmail={userEmail} resetForm={resetForm} resetLink={resetLink} logoUrl={finalLogoUrl} showLogo={showLogo}>
            <p className='text-sm text-neutral-sec m-0 mt-6'>Please wait while we look up your credentials...</p>
          </LoginOptionsHeader>
        </>
      ) : loginOptions?.accountLocked ? (
        <>
          <LoginOptionsHeader userEmail={userEmail} resetForm={resetForm} resetLink={resetLink} logoUrl={finalLogoUrl} showLogo={showLogo}>
            <div className='flex flex-col gap-3 mt-6'>
              <p className='flex items-start gap-1.5 m-0 text-xs text-error'>
                <i className="fas fa-exclamation-circle mt-0.5" aria-hidden="true"></i>
                <span>Your account is locked</span>
              </p>
              <StyledButton variant='primary' className='w-full' to={forgotPasswordLink}>
                Reset password
              </StyledButton>
            </div>
          </LoginOptionsHeader>
        </>
      ) : (
        <>
          <LoginOptionsHeader userEmail={userEmail} resetForm={resetForm} resetLink={resetLink} logoUrl={finalLogoUrl} showLogo={showLogo} />
          <div className='flex flex-col gap-6 mt-6'>
            {error && (
              <p className='flex items-start gap-1.5 m-0 text-xs text-error' role='alert'>
                <i className="fas fa-exclamation-circle mt-0.5" aria-hidden="true"></i>
                <span>{error}</span>
              </p>
            )}
            {loginOptions?.SSOLinks?.length > 0 && (
              <div className='flex flex-col gap-3'>
                {loginOptions.SSOLinks.map((link, i) => (
                  <StyledButton
                    key={link.name + '_' + i}
                    // e2e hook - the SSO specs used to select on the button copy, which this
                    // redesign broke. keep this attribute stable when the copy changes.
                    data-cy={`sso-login-${link.name}`}
                    variant='primary'
                    className='w-full'
                    leftIcon={link.name === SLP_SSO_PROVIDER ? (
                      <img className='h-4 w-auto block' src='/img/SP-symbol-white.png' alt='' />
                    ) : undefined}
                    href={loginRedirect ? link.link + `?RelayState=${encodeURIComponent(`${loginRedirect}`)}` : link.link}
                  >
                    Continue with {link.name}
                  </StyledButton>
                ))}
                {hasOtherOptions && (
                  <div className='flex items-center gap-3 my-3'>
                    <span className='flex-1 border-t border-neutral-pri'></span>
                    <span className='text-xs font-medium uppercase tracking-wider text-neutral-ter'>or</span>
                    <span className='flex-1 border-t border-neutral-pri'></span>
                  </div>
                )}
              </div>
            )}
            {loginOptions?.hasPassword ? (
              <form name='userLoginForm' data-cy='login-password-form' onSubmit={handleSubmit} className='w-full flex flex-col gap-3 [&>div]:mb-0'>
                <div className='flex items-baseline justify-between gap-3'>
                  <label htmlFor='password' className='text-xs font-medium text-neutral-pri'>Enter password</label>
                  <Link to={forgotPasswordLink} className='flex-none whitespace-nowrap text-xs'>Password trouble?</Link>
                </div>
                <PasswordInput
                  name='password'
                  value={user.password}
                  change={e => setUser({ ...user, [e.target.name]: e.target.value })}
                  required={true}
                  autoFocus={true}
                />
                <StyledButton
                  variant={hasSSO ? 'secondary' : 'primary'}
                  className='w-full'
                  type='submit'
                  disabled={!user.password}
                >
                  Sign in
                </StyledButton>
              </form>
            ) : loginOptions?.hasPassword === false ? (
              <div className='flex flex-col gap-3'>
                <p className='text-sm text-neutral-sec m-0'>Looks like you don't have a password</p>
                <StyledButton
                  variant={hasSSO ? 'secondary' : 'primary'}
                  className='w-full'
                  to={forgotPasswordLink}
                >
                  Set up password
                </StyledButton>
              </div>
            ) : null}
            {loginOptions?.allowMagicLink && (
              <div className='flex flex-col gap-3'>
                <p className='text-sm text-neutral-sec m-0'>Sign in with a one-time link</p>
                <StyledButton
                  variant='secondary'
                  className='w-full'
                  onClick={() => alert('TODO: This feature is not yet implemented')}
                >
                  Send link to email
                </StyledButton>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

UserLoginFlow.propTypes = {
  resetLink: PropTypes.string
  , loginRedirect: PropTypes.string
  , originRedirect: PropTypes.string
  , wrapperClass: PropTypes.string
  , logoUrl: PropTypes.string
  , welcomeMessage: PropTypes.string
  , showLogo: PropTypes.bool
  , emailStepFooter: PropTypes.node
}

const LoginOptionsHeader = ({ userEmail, resetForm, resetLink, children, logoUrl, showLogo }) => {

  return (
    <div className='flex flex-col gap-6'>
      {showLogo && (
        <div className='flex justify-center p-2 md:p-4'>
          <img className="w-auto h-auto max-h-[30px]" src={logoUrl} alt='logo' />
        </div>
      )}
      {/* identity row - who you're signing in as, plus a way back to the email step */}
      <div className='flex items-center justify-between gap-3 pb-4 border-b border-neutral-pri'>
        <span className='flex-1 min-w-0 truncate text-sm font-medium text-neutral-pri'>{userEmail}</span>
        {resetLink ? (
          <Link
            className='flex-none flex items-center justify-center w-8 h-8 rounded text-neutral-ter hover:text-neutral-pri hover:no-underline'
            to={isSafeUrl(resetLink)}
            onClick={resetForm}
            title='Use a different email'
            aria-label='Use a different email'
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </Link>
        ) : (
          <button
            type='button'
            className='flex-none flex items-center justify-center w-8 h-8 rounded text-neutral-ter hover:text-neutral-pri'
            onClick={resetForm}
            title='Use a different email'
            aria-label='Use a different email'
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

LoginOptionsHeader.propTypes = {
  userEmail: PropTypes.string
  , resetForm: PropTypes.func
  , resetLink: PropTypes.string
  , logoUrl: PropTypes.string
  , showLogo: PropTypes.bool
  , children: PropTypes.node
}


const getFriendlyError = (error) => {
  switch (error) {
    // ref returns on SessionController samlLogin and samlLoginCallback
    case 'provider_error_1':
      return 'Failed to Initiate SAML Login with Provider';
    case 'provider_error_2':
      return 'Error Authenticating with SAML Login Provider';
    case 'provider_error_3':
      return 'Failed to Authenticate with SAML Login Provider';
    case 'no_matching_user_1':
      return 'Unable to Locate Matching User. Please try again.';
    case 'no_matching_user_2':
      return 'Unable to Locate Matching User. Please try again.';
    case 'not_allowed_1':
      return 'Permission Denied. Please try again.';
    case 'not_allowed_2':
      return 'Unable To Log In As Global Admin Via SSO. Please Contact Admin.';
    default:
      return error;
  }
}

export default UserLoginFlow;
